/**
 * useColumnResize Hook
 * 
 * Provides column resizing functionality for tables with:
 * - Manual resizing by dragging column borders
 * - Auto-fit by double-clicking column borders
 * - Persistent column widths
 * 
 * @example
 * ```tsx
 * const { columnWidths, handleMouseDown, handleDoubleClick } = useColumnResize({
 *   tableId: 'my-table',
 *   columns: ['column1', 'column2'],
 *   defaultWidths: { column1: 150, column2: 200 }
 * });
 * 
 * <th style={{ width: columnWidths.column1 }}>
 *   <div>Column 1</div>
 *   <div 
 *     onMouseDown={(e) => handleMouseDown(e, 'column1')}
 *     onDoubleClick={(e) => handleDoubleClick(e, 'column1')}
 *   />
 * </th>
 * ```
 */

import { useState, useCallback, useRef, useEffect } from 'react';

interface UseColumnResizeProps {
  tableId: string;
  columns: string[];
  defaultWidths?: Record<string, number>;
  minWidth?: number;
  maxWidth?: number;
}

interface ColumnWidths {
  [key: string]: number;
}

export function useColumnResize({
  tableId,
  columns,
  defaultWidths = {},
  minWidth = 80,
  maxWidth = 800,
}: UseColumnResizeProps) {
  // Initialize column widths from localStorage or defaults
  const [columnWidths, setColumnWidths] = useState<ColumnWidths>(() => {
    const stored = localStorage.getItem(`table-${tableId}-widths`);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return defaultWidths;
      }
    }
    return defaultWidths;
  });

  const resizingColumn = useRef<string | null>(null);
  const startX = useRef<number>(0);
  const startWidth = useRef<number>(0);
  const tableRef = useRef<HTMLTableElement | null>(null);
  const hasAutoExpanded = useRef<boolean>(false);

  // Save to localStorage whenever widths change
  useEffect(() => {
    localStorage.setItem(`table-${tableId}-widths`, JSON.stringify(columnWidths));
  }, [columnWidths, tableId]);

  // Handle mouse down on resize handle
  const handleMouseDown = useCallback((e: React.MouseEvent, columnKey: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    resizingColumn.current = columnKey;
    startX.current = e.clientX;
    startWidth.current = columnWidths[columnKey] || 150;

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [columnWidths]);

  // Handle mouse move during resize
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!resizingColumn.current) return;

    const diff = e.clientX - startX.current;
    const newWidth = Math.max(minWidth, Math.min(maxWidth, startWidth.current + diff));

    setColumnWidths(prev => ({
      ...prev,
      [resizingColumn.current!]: newWidth,
    }));
  }, [minWidth, maxWidth]);

  // Handle mouse up to end resize
  const handleMouseUp = useCallback(() => {
    resizingColumn.current = null;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  // Calculate optimal width for a column
  const calculateColumnWidth = useCallback((columnKey: string, table: HTMLTableElement): number => {
    const columnIndex = columns.indexOf(columnKey);
    if (columnIndex === -1) return minWidth;

    const cells = table.querySelectorAll(`tr > *:nth-child(${columnIndex + 1})`);
    let calculatedMaxWidth = minWidth;

    cells.forEach(cell => {
      const element = cell as HTMLElement;
      // Create a temporary element to measure content width
      const temp = document.createElement('span');
      temp.style.visibility = 'hidden';
      temp.style.position = 'absolute';
      temp.style.whiteSpace = 'nowrap';
      temp.textContent = element.textContent || '';
      
      // Copy font styles for accurate measurement
      const styles = window.getComputedStyle(element);
      temp.style.font = styles.font;
      temp.style.fontSize = styles.fontSize;
      temp.style.fontWeight = styles.fontWeight;
      temp.style.fontFamily = styles.fontFamily;
      temp.style.padding = styles.padding;
      
      document.body.appendChild(temp);
      const width = temp.offsetWidth + 32; // Add padding
      document.body.removeChild(temp);
      
      calculatedMaxWidth = Math.max(calculatedMaxWidth, width);
    });

    return Math.min(calculatedMaxWidth, maxWidth);
  }, [columns, minWidth, maxWidth]);

  // Auto-fit column on double click
  const handleDoubleClick = useCallback((e: React.MouseEvent, columnKey: string, table?: HTMLTableElement | null) => {
    e.preventDefault();
    e.stopPropagation();

    if (!table) {
      // Try to find the table element
      const target = e.currentTarget as HTMLElement;
      table = target.closest('table');
    }

    if (!table) return;

    const width = calculateColumnWidth(columnKey, table);
    setColumnWidths(prev => ({
      ...prev,
      [columnKey]: width,
    }));
  }, [calculateColumnWidth]);

  // Auto-expand all columns to fit content
  const autoExpandAllColumns = useCallback((table: HTMLTableElement) => {
    const newWidths: ColumnWidths = {};
    
    columns.forEach(columnKey => {
      newWidths[columnKey] = calculateColumnWidth(columnKey, table);
    });

    setColumnWidths(newWidths);
  }, [columns, calculateColumnWidth]);

  // Set up global mouse event listeners
  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  // Reset all columns to default
  const resetColumnWidths = useCallback(() => {
    setColumnWidths(defaultWidths);
  }, [defaultWidths]);

  // Reset specific column
  const resetColumn = useCallback((columnKey: string) => {
    setColumnWidths(prev => ({
      ...prev,
      [columnKey]: defaultWidths[columnKey] || 150,
    }));
  }, [defaultWidths]);

  // Set table ref and auto-expand on first render
  const setTableRef = useCallback((table: HTMLTableElement | null) => {
    if (table && !hasAutoExpanded.current) {
      tableRef.current = table;
      // Check if we have stored widths, if not, auto-expand
      const stored = localStorage.getItem(`table-${tableId}-widths`);
      if (!stored) {
        // Delay to ensure table is fully rendered
        setTimeout(() => {
          autoExpandAllColumns(table);
          hasAutoExpanded.current = true;
        }, 100);
      } else {
        hasAutoExpanded.current = true;
      }
    }
  }, [tableId, autoExpandAllColumns]);

  return {
    columnWidths,
    handleMouseDown,
    handleDoubleClick,
    resetColumnWidths,
    resetColumn,
    autoExpandAllColumns,
    setTableRef,
  };
}
